import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useUpdatedFields } from '@ors/contexts/Projects/UpdatedFieldsContext'
import ProjectsHeader from '../ProjectSubmission/ProjectsHeader'
import ProjectsCreate from '../ProjectsCreate/ProjectsCreate'
import useGetRelatedProjects from '../hooks/useGetRelatedProjects'
import { useGetTrancheErrors } from '../hooks/useGetTrancheErrors'
import { useGetProjectFiles } from '../hooks/useGetProjectFiles'
import { fetchSpecificFields } from '../hooks/getSpecificFields'
import {
  filterApprovalFields,
  getConsumptionLevelStatus,
  getDefaultValues,
  getFieldData,
  getFileFromMetadata,
  getFormattedDecimalValue,
  getOdsOdpFields,
  getProjectDuration,
  hasSpecificField,
} from '../utils'
import {
  OdsOdpFields,
  ProjectData,
  ProjectFile,
  ProjectFilesObject,
  ProjectSpecificFields,
  ProjectTypeApi,
  SpecificFields,
  TrancheErrorType,
  BpDataProps,
  FileMetaDataType,
} from '../interfaces'
import {
  approvalOdsFields,
  considerationOpts,
  defaultTrancheErrors,
  initialCrossCuttingFields,
  initialProjectIdentifiers,
} from '../constants'
import ProjectsDataContext from '@ors/contexts/Projects/ProjectsDataContext'
import PermissionsContext from '@ors/contexts/PermissionsContext'
import useVisibilityChange from '@ors/hooks/useVisibilityChange'
import { useStore } from '@ors/store'
import { api } from '@ors/helpers'

import { enqueueSnackbar } from 'notistack'
import {
  debounce,
  groupBy,
  map,
  filter,
  find,
  isArray,
  pick,
  mapKeys,
} from 'lodash'

const ProjectsEdit = ({
  project,
  mode,
  postExComUpdate = false,
  approval = false,
  impact = false,
}: {
  project: ProjectTypeApi
  mode: string
  postExComUpdate?: boolean
  approval?: boolean
  impact?: boolean
}) => {
  const project_id = project.id.toString()
  const isEditMode = mode === 'edit'
  const isVersion3 = isEditMode && project.version >= 3

  const { canViewProjects, canEditApprovedProjects, canViewBp } =
    useContext(PermissionsContext)
  const {
    countries,
    clusters,
    project_types,
    sectors,
    subsectors,
    consumptionLevelStatuses,
  } = useContext(ProjectsDataContext)

  const { updatedFields, addUpdatedField, clearUpdatedFields } =
    useUpdatedFields()

  useEffect(() => {
    clearUpdatedFields()
  }, [])

  const shouldEmptyField = (data: any, crtDataId: number) => {
    const isObsoleteField = find(
      data,
      (entry) => entry.id === crtDataId,
    )?.obsolete
    return mode !== 'edit' && isObsoleteField
  }
  const shouldEmptyCluster = shouldEmptyField(clusters, project.cluster_id)
  const shouldEmptySubsector =
    mode !== 'edit' &&
    find(subsectors, (subsector) =>
      map(project.subsectors, 'id').includes(subsector.id),
    )?.obsolete

  const [projectData, setProjectData] = useState<ProjectData>({
    projIdentifiers: initialProjectIdentifiers,
    bpLinking: { isLinkedToBP: false, bpId: null },
    crossCuttingFields: initialCrossCuttingFields,
    projectSpecificFields: {} as SpecificFields,
    approvalFields: {} as SpecificFields,
  })
  const [specificFields, setSpecificFields] = useState<ProjectSpecificFields[]>(
    [],
  )
  const [specificFieldsLoaded, setSpecificFieldsLoaded] =
    useState<boolean>(false)
  const [shouldValidateTotalFund, setShouldValidateTotalFund] = useState(true)
  const [canViewTabs, setCanViewTabs] = useState<boolean>(false)

  const { projIdentifiers, crossCuttingFields } = projectData
  const { country, agency, cluster } = projIdentifiers
  const { project_type, sector } = crossCuttingFields

  const groupedFields = groupBy(specificFields, 'table')
  const fieldsOfProject = groupedFields['project'] || []
  const projectFields = isEditMode
    ? fieldsOfProject
    : filter(fieldsOfProject, (field) => !field.is_actual)
  const odsOdpFields = getOdsOdpFields(specificFields)

  const fieldsValuesLoaded = useRef<boolean>(false)
  const filesLoaded = useRef<boolean>(false)
  const approvalFieldsValuesLoaded = useRef<boolean>(false)

  const { files: data, loadedFiles } = useGetProjectFiles(parseInt(project_id))
  const areFilesLoaded = loadedFiles && filesLoaded.current

  const {
    fetchProjectFields,
    projectFields: allFields,
    setViewableFields,
    setEditableFields,
  } = useStore((state) => state.projectFields)

  const debouncedFetchProjectFields = useMemo(
    () => debounce(() => fetchProjectFields?.(), 0),
    [fetchProjectFields],
  )

  useEffect(() => {
    debouncedFetchProjectFields()
  }, [])

  useEffect(() => {
    if (allFields && allFields.loaded && allFields.data) {
      const version = isEditMode ? project.version : 1
      const submissionStatus = isEditMode
        ? project.submission_status
        : undefined

      setViewableFields?.(version, submissionStatus)
      setEditableFields?.(
        version,
        submissionStatus,
        canEditApprovedProjects,
        postExComUpdate,
        mode,
      )
    }
  }, [allFields, setViewableFields, setEditableFields])

  const approvalFields = useMemo(
    () =>
      isVersion3 && isArray(allFields)
        ? allFields.filter((field) =>
            filterApprovalFields(specificFields, field),
          )
        : [],
    [allFields, specificFields],
  )

  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([])
  const [files, setFiles] = useState<ProjectFilesObject>({
    deletedFilesIds: [],
    newFiles: [],
  })
  const [filesMetaData, setFilesMetaData] = useState<FileMetaDataType[]>([])

  useEffect(() => {
    if (!loadedFiles) return

    setProjectFiles(data)
    filesLoaded.current = true

    if ((mode === 'copy' || mode === 'full-link') && data?.length > 0) {
      const loadFiles = async () => {
        const resolvedFiles = await Promise.all(
          data.map((file: ProjectFile) => getFileFromMetadata(file)),
        )

        setFiles((prev) => ({
          ...prev,
          newFiles: resolvedFiles,
        }))

        setFilesMetaData((prev) => [
          ...prev,
          ...map(data, (file, index) => ({
            id: null,
            name: file.filename,
            size: resolvedFiles[index].size,
            type: file.type,
          })),
        ])
      }

      loadFiles()
    }
  }, [data, loadedFiles])

  useEffect(() => {
    if (isEditMode) {
      setFiles({
        deletedFilesIds: [],
        newFiles: [],
      })
      setFilesMetaData(projectFiles)
    }
  }, [projectFiles])

  const [metaProjectId, setMetaProjectId] = useState<number | null>(
    project.meta_project_id,
  )
  const [refetchRelatedProjects, setRefetchRelatedProjects] = useState(false)

  const relatedProjects = useGetRelatedProjects(
    mode,
    metaProjectId,
    refetchRelatedProjects,
    project,
  )

  const [bpData, setBpData] = useState({
    hasBpData: false,
    bpDataLoading: false,
  })

  const [errors, setErrors] = useState<{ [key: string]: [] }>({})
  const [fileErrors, setFileErrors] = useState<string>('')
  const [trancheErrors, setTrancheErrors] =
    useState<TrancheErrorType>(defaultTrancheErrors)

  useEffect(() => {
    setProjectData((prevData) => ({
      ...prevData,
      projIdentifiers: {
        ...prevData.projIdentifiers,
        country: project.country_id,
        meeting: project.meeting_id,
        agency: project.agency_id,
        lead_agency: project.lead_agency,
        lead_agency_submitting_on_behalf:
          project.lead_agency_submitting_on_behalf,
        cluster: !shouldEmptyCluster ? project.cluster_id : null,
        production: !shouldEmptyCluster ? project.production : false,
        category: !shouldEmptyCluster
          ? (project.cluster?.category ?? null)
          : null,
        post_excom_meeting:
          mode === 'edit' ? project.post_excom_meeting_id : null,
        post_excom_decision:
          mode === 'edit' ? project.post_excom_decision_id : null,
      },
      ...(mode !== 'partial-link'
        ? {
            bpLinking: {
              isLinkedToBP: !!project.bp_activity,
              bpId: project.bp_activity?.id ?? null,
            },
            crossCuttingFields: {
              project_type: !shouldEmptyField(
                project_types,
                project.project_type_id,
              )
                ? project.project_type_id
                : null,
              sector: !shouldEmptyField(sectors, project.sector_id)
                ? project.sector_id
                : null,
              subsector_ids: !shouldEmptySubsector
                ? map(project.subsectors, 'id')
                : [],
              consumption_level_status: project.consumption_level_status,
              title: project.title,
              description: project.description,
              project_start_date: project.project_start_date,
              project_end_date: project.project_end_date,
              project_duration:
                project.project_duration ?? getProjectDuration(project),
              total_fund: getFormattedDecimalValue(project.total_fund),
              support_cost_psc: getFormattedDecimalValue(
                project.support_cost_psc,
              ),
              blanket_or_individual_consideration:
                isEditMode &&
                (project.submission_status !== 'Draft' || project.version === 2)
                  ? (considerationOpts.find(
                      (opt) =>
                        opt.value ===
                        project.blanket_or_individual_consideration,
                    )?.id ?? null)
                  : null,
              ...(isEditMode &&
                project.submission_status === 'Approved' && {
                  adjustment: project.adjustment ?? false,
                  interest: getFormattedDecimalValue(project.interest ?? null),
                }),
            },
          }
        : {
            bpLinking: { isLinkedToBP: false, bpId: null },
          }),
    }))
  }, [])

  useEffect(() => {
    if (mode === 'partial-link') {
      setProjectData((prevData) => ({
        ...prevData,
        crossCuttingFields: {
          ...initialCrossCuttingFields,
          consumption_level_status: getConsumptionLevelStatus(
            countries,
            project.country_id,
            consumptionLevelStatuses,
          ),
        },
      }))
    }
  }, [countries, consumptionLevelStatuses])

  useEffect(() => {
    if (!approval && !impact && canViewBp && country && agency && cluster) {
      setBpData({
        hasBpData: false,
        bpDataLoading: true,
      })
    }
  }, [country, agency, cluster])

  const onBpDataChange = (bpData: BpDataProps) => {
    setBpData(bpData)
  }

  useEffect(() => {
    setSpecificFieldsLoaded(false)

    if (cluster && project_type && sector) {
      fetchSpecificFields(
        cluster,
        project_type,
        sector,
        setSpecificFields,
        isEditMode ? project_id : null,
        setSpecificFieldsLoaded,
        setShouldValidateTotalFund,
      )
    } else {
      setSpecificFields([])
      setShouldValidateTotalFund(true)

      if (fieldsValuesLoaded.current) {
        setSpecificFieldsLoaded(true)
      }

      setCanViewTabs(true)
    }
  }, [cluster, project_type, sector])

  useEffect(() => {
    if (
      isVersion3 &&
      !approvalFieldsValuesLoaded.current &&
      approvalFields.length > 0
    ) {
      const approvalFieldsNames = approvalFields.map((f) => f.write_field_name)

      const totalFieldsValues = pick(
        Object.fromEntries(
          approvalOdsFields.map((field) => [
            field,
            getFormattedDecimalValue(
              project[field as keyof ProjectTypeApi] ?? undefined,
            ),
          ]),
        ),
        approvalFieldsNames,
      )

      const filteredFieldsValues = pick(
        Object.fromEntries(
          approvalOdsFields.map((field) => [
            field,
            getFormattedDecimalValue(
              project[field as keyof ProjectTypeApi] ??
                project[`computed_${field}` as keyof ProjectTypeApi],
            ),
          ]),
        ),
        approvalFieldsNames,
      )

      const computedFieldsValues = mapKeys(
        filteredFieldsValues,
        (_, key) => `computed_${key}`,
      )

      setProjectData((prevData) => ({
        ...prevData,
        approvalFields: {
          ...getDefaultValues<ProjectTypeApi>(approvalFields, project),
          meeting: project.meeting_id,
          decision: project.decision_id,
          date_completion:
            project.submission_status === 'Recommended'
              ? project.project_end_date
              : project.date_completion,
          funding_window: project.funding_window,
          ...computedFieldsValues,
          ...totalFieldsValues,
        },
      }))

      if (fieldsValuesLoaded.current) {
        approvalFieldsValuesLoaded.current = true
      }
    }
  }, [approvalFields])

  useEffect(() => {
    if (!fieldsValuesLoaded.current && specificFields.length > 0) {
      setProjectData((prevData) => ({
        ...prevData,
        ...(mode !== 'partial-link'
          ? {
              projectSpecificFields: {
                ...getDefaultValues<ProjectTypeApi>(projectFields, project),
                ods_odp: map(project.ods_odp, (ods) => {
                  return {
                    ...getDefaultValues<OdsOdpFields>(
                      odsOdpFields,
                      ods,
                      getFieldData(specificFields, 'group')
                        ? project
                        : undefined,
                    ),
                  }
                }),
              },
            }
          : {
              projectSpecificFields: {
                ...getDefaultValues<ProjectTypeApi>(projectFields),
                ods_odp: [],
              },
            }),
      }))

      fieldsValuesLoaded.current = true
    }

    if (
      !fieldsValuesLoaded.current &&
      (mode === 'partial-link' ||
        (specificFieldsLoaded && specificFields.length === 0))
    ) {
      fieldsValuesLoaded.current = true
    }

    if (
      !fieldsValuesLoaded.current &&
      isEditMode &&
      project.submission_status === 'Approved' &&
      !(project.cluster && project.project_type && project.sector)
    ) {
      fieldsValuesLoaded.current = true
    }
  }, [specificFields, fieldsValuesLoaded])

  const tranche = projectData.projectSpecificFields?.tranche ?? 0

  const getTrancheErrors = async () => {
    setTrancheErrors((prevErrors) => {
      return { ...prevErrors, loaded: false, loading: true }
    })

    try {
      const result = await api(
        `api/projects/v2/list_previous_tranches/country/${country}/cluster/${cluster}/tranche/${tranche}`,
        {
          params: {
            project_id: mode === 'edit' ? project_id : undefined,
            include_validation: true,
          },
          withStoreCache: false,
        },
        false,
      )

      return useGetTrancheErrors(result, projectFields, setTrancheErrors)
    } catch (error) {
      setTrancheErrors({ ...defaultTrancheErrors, loaded: true })
      enqueueSnackbar(
        <>
          An error occurred during previous tranches validation. Please try
          again.
        </>,
        { variant: 'error' },
      )

      return true
    }
  }

  const debouncedGetTrancheErrors = debounce(getTrancheErrors, 0)

  useEffect(() => {
    const hasTrancheField = hasSpecificField(specificFields, 'tranche')

    if (tranche <= 1 || !hasTrancheField) {
      setTrancheErrors({ ...defaultTrancheErrors, loaded: true })
    } else if (canViewProjects) {
      debouncedGetTrancheErrors()
    }
  }, [country, cluster, tranche, project_id, specificFields])

  const setProjectDataWithEditTracking = (
    updater: React.SetStateAction<ProjectData>,
    fieldName?: string,
  ) => {
    setProjectData((prevData) => {
      if (fieldName) {
        addUpdatedField(fieldName)
      }

      return typeof updater === 'function'
        ? (updater as (prev: ProjectData) => ProjectData)(prevData)
        : updater
    })
  }

  useVisibilityChange(updatedFields.size > 0)

  return (
    canViewTabs && (
      <>
        <ProjectsHeader
          {...{
            mode,
            postExComUpdate,
            project,
            projectData,
            projectFiles,
            files,
            setErrors,
            setFileErrors,
            setProjectFiles,
            specificFields,
            trancheErrors,
            getTrancheErrors,
            approvalFields,
            specificFieldsLoaded,
            setProjectData,
            bpData,
            filesMetaData,
            shouldValidateTotalFund,
          }}
          loadedFiles={areFilesLoaded}
        />
        <ProjectsCreate
          {...{
            projectData,
            mode,
            postExComUpdate,
            approval,
            impact,
            specificFields,
            project,
            files,
            setFiles,
            projectFiles,
            errors,
            fileErrors,
            trancheErrors,
            getTrancheErrors,
            relatedProjects,
            approvalFields,
            bpData,
            onBpDataChange,
            filesMetaData,
            setFilesMetaData,
            metaProjectId,
            setMetaProjectId,
            setRefetchRelatedProjects,
            shouldValidateTotalFund,
            setErrors,
          }}
          setProjectData={setProjectDataWithEditTracking}
          specificFieldsLoaded={
            (specificFieldsLoaded && fieldsValuesLoaded.current) ||
            !(cluster && project_type && sector)
          }
          loadedFiles={areFilesLoaded}
        />
      </>
    )
  )
}

export default ProjectsEdit
